import React from 'react'
import { Link } from 'react-router-dom'
import PageMeta from '../components/PageMeta'
import ContactForm from '../components/ContactForm'
import GalleryLightbox from '../components/GalleryLightbox'
import FeaturePillars from '../components/FeaturePillars'
import BottomContactSection from '../components/BottomContactSection'
import { PAGES_DATA, COMPANY_INFO, COMMON_FEATURES } from '../data/pagesData'

export default function HomePage() {
  const page = PAGES_DATA.index

  return (
    <div>
      <PageMeta title={page.title} description={page.metaDescription} />

      {/* Hero Section */}
      <section className="relative h-[750px] bg-[#070707] flex items-center py-20 overflow-hidden">
        {/* Background Video */}
        <video
          src="https://assets.auxodev.dk/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/hero-2.mp4"
          poster={page.heroImage}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(180deg, #00000040 0%, #000000 100%)' }}
        />

        {/* Hero Content (following container lines) */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="max-w-2xl flex flex-col gap-4 text-white">
            <span className="text-xs font-bold uppercase tracking-widest text-white/90">
              {page.heroTag || "Velkommen til Sakskær Auto"}
            </span>
            <h1 className="text-4xl md:text-[55px] font-black tracking-tight leading-tight text-white uppercase">
              {page.heroTitle}
            </h1>
            <p className="text-base md:text-lg text-slate-200 font-normal">
              {page.heroSub}
            </p>

            {/* CTAs in Sentence Case */}
            <div className="flex flex-wrap gap-4 mt-2">
              <Link
                to="/kontakt/"
                className="bg-[#046BB4] hover:bg-[#003E5E] text-white font-bold px-6 py-3 transition rounded-sm shadow-md cursor-pointer"
              >
                Kontakt os
              </Link>
              <Link
                to="/service/"
                className="bg-[#003E5E] hover:bg-[#046BB4] text-white font-bold px-6 py-3 transition rounded-sm shadow-md cursor-pointer"
              >
                Læs mere om service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Pillars */}
      <FeaturePillars />

      {/* Main Section: Dit lokale autoværksted */}
      <section className="bg-white py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="bg-white rounded-2xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            
            {/* Left Column: Featured Image */}
            <div className="relative w-full h-full min-h-[320px] lg:min-h-0 rounded-xl overflow-hidden">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/imgi-23-495389949-17877993513318194-771210968845943644-n.jpg" 
                alt="Bilværksted reparation" 
                className="w-full h-full object-cover rounded-xl lg:absolute lg:inset-0"
              />
            </div>

            {/* Right Column: Content */}
            <div className="flex flex-col items-start space-y-4">
              {/* Eyebrow */}
              <span className="text-xs font-bold uppercase tracking-wider text-[#0b3356]">
                {page.sectionSub}
              </span>

              {/* Main Title */}
              <h2 className="text-2xl sm:text-3xl lg:text-3xl font-extrabold uppercase tracking-tight text-neutral-900 leading-tight">
                {page.sectionTitle}
              </h2>

              {/* Introduction */}
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                {page.sectionText}
              </p>

              {/* List Section Header */}
              <h3 className="text-base font-bold text-neutral-900 pt-2">
                {page.servicesListTitle}
              </h3>

              {/* Feature List */}
              <ul className="space-y-1.5 list-disc list-inside text-sm sm:text-base text-slate-600">
                {page.servicesList.map((item, idx) => (
                  <li key={idx} className="pl-1">
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Outro Paragraph */}
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed pt-1">
                {page.sectionText2}
              </p>

              {/* CTA Button */}
              <div className="pt-3">
                <Link 
                  to="/kontakt/" 
                  className="inline-block px-6 py-3 bg-[#0e68b8] hover:bg-[#0b5699] text-white font-semibold text-sm sm:text-base rounded-md transition-colors duration-200 shadow-sm cursor-pointer"
                >
                  Kontakt os i dag
                </Link>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 3 Focus Boxes Section */}
      <section className="bg-[#ececec] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          
          {/* Card 1: Dæk og Fælge */}
          <Link to="/daek-og-faelge/" className="flex flex-col group cursor-pointer">
            <div className="overflow-hidden aspect-[16/10] w-full bg-gray-300">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/483884455-122123454662418369-9044174517283196909-n.jpg" 
                alt="Dæk og Fælge" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="mt-5 mb-2 text-xl font-bold uppercase tracking-wider text-[#24272a] group-hover:text-[#0b63ab] transition-colors">
              DÆK OG FÆLGE
            </h3>
            <p className="text-[#4e5358] text-sm sm:text-base leading-relaxed">
              Hos Sakskær Auto i Varde tilbyder vi alt inden for dæk og fælge til konkurrencedygtige priser.
            </p>
          </Link>

          {/* Card 2: Gearkasse Service */}
          <Link to="/gearkasse-service/" className="flex flex-col group cursor-pointer">
            <div className="overflow-hidden aspect-[16/10] w-full bg-gray-300">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/content-7-scaled.jpg" 
                alt="Gearkasse Service" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="mt-5 mb-2 text-xl font-bold uppercase tracking-wider text-[#24272a] group-hover:text-[#0b63ab] transition-colors">
              GEARKASSE SERVICE
            </h3>
            <p className="text-[#4e5358] text-sm sm:text-base leading-relaxed">
              Hos Sakskær Auto i Varde anbefaler vi gearkasse service – også på biler, hvor producenten ikke har fastsat officielle intervaller.
            </p>
          </Link>

          {/* Card 3: Service */}
          <Link to="/service/" className="flex flex-col group cursor-pointer">
            <div className="overflow-hidden aspect-[16/10] w-full bg-gray-300">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/523175374-17887908078318194-7009705061051321505-n.jpg" 
                alt="Service" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="mt-5 mb-2 text-xl font-bold uppercase tracking-wider text-[#24272a] group-hover:text-[#0b63ab] transition-colors">
              SERVICE
            </h3>
            <p className="text-[#4e5358] text-sm sm:text-base leading-relaxed">
              Hos Sakskær Auto i Varde tilbyder vi professionel bilservice efter producentens anvisninger, så du bibeholder bilens fabriksgaranti.
            </p>
          </Link>

        </div>
      </section>

      {/* Guarantee / Value Proposition Section */}
      <section className="w-full bg-white py-12 lg:py-[100px] border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          
          {/* Left Column: Text Content */}
          <div className="flex flex-col space-y-6">
            
            {/* Eyebrow Tag */}
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#0c66ac]">
              {page.guaranteeSub || "Service og reparation til fair priser – uden overraskelser"}
            </span>

            {/* Main Heading */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-black">
              {page.guaranteeTitle || "Hos os får du altid:"}
            </h2>

            {/* Bullet List */}
            <ul className="space-y-2.5 text-base text-gray-600 list-disc list-inside marker:text-gray-700">
              {(page.guaranteeList || [
                "Ærlig rådgivning og klare aftaler",
                "Hurtig og effektiv service",
                "Dygtige mekanikere med stor erfaring",
                "Fokus på både kvalitet og pris"
              ]).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            {/* Callout Paragraph */}
            <p className="text-base text-gray-600 leading-relaxed pt-2">
              Book tid i dag, og oplev forskellen på et autoværksted, der sætter{" "}
              <strong className="font-bold text-gray-900">dig og din bil først!</strong>
            </p>

            {/* Primary Action Button */}
            <div className="pt-2">
              <Link
                to="/kontakt/"
                className="inline-flex items-center justify-center bg-[#0c66ac] hover:bg-[#084d84] text-white font-bold px-7 py-3.5 rounded-sm transition-colors duration-200 shadow-xs"
              >
                Kontakt os i dag
              </Link>
            </div>
          </div>

          {/* Right Column: Media */}
          <div className="relative w-full h-full min-h-[380px] sm:min-h-[440px] lg:min-h-0 lg:-my-6 rounded-2xl overflow-hidden shadow-md">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/524965098-17887908123318194-4270870576614691254-n-1000x1000.jpg" 
              alt="Autoværksted service og reservedele" 
              className="w-full h-full object-cover rounded-2xl lg:absolute lg:inset-0"
            />
          </div>

        </div>
      </div>
    </section>

      {/* Facebook & Gallery Showcase Section */}
      <section className="bg-[#0a0a0b] text-white py-16 lg:py-24 px-4 sm:px-6 lg:px-8 border-b border-neutral-900">
        {/* Section Header */}
        <header className="text-center mb-12 flex flex-col items-center gap-3">
          <h2 className="font-bold text-xl sm:text-2xl md:text-3xl tracking-widest uppercase text-white">
            Følg med på vores Facebook
          </h2>
          <div className="text-neutral-600 hover:text-neutral-400 transition-colors">
            <a 
              href={COMPANY_INFO.facebookUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Besøg vores Facebook side"
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                <path d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.374 14.5 5 15.5 5H18V0h-3.808C10.592 0 9 1.583 9 4.615V8z"/>
              </svg>
            </a>
          </div>
        </header>

        {/* Content Grid */}
        <div className="max-w-[1600px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: 3x2 Photo Matrix */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-3 lg:gap-4 w-full lg:w-auto">
            {[
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/523586023-17887908087318194-8587928854735141438-n-1000x1000.jpg",
                alt: "Dæk og fælge montering"
              },
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/524965098-17887908123318194-4270870576614691254-n-1000x1000.jpg",
                alt: "Gearkasse service og skylning"
              },
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/496749061-17877993531318194-1459227221856858672-n-1000x1000.jpg",
                alt: "Autoværksted service"
              },
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/20250705-133330-scaled.jpg",
                alt: "Bilreparation på værksted"
              },
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/20240619-101805-750x1000.jpg",
                alt: "Professionelt biludstyr"
              },
              {
                src: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/tilfaeldigt-sted-750x1000.jpg",
                alt: "Kunder og værksted"
              }
            ].map((img, idx) => (
              <a
                key={idx}
                href={COMPANY_INFO.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full aspect-square bg-neutral-900 overflow-hidden block group relative rounded-sm"
              >
                <img 
                  src={img.src} 
                  alt={img.alt} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <i className="fa-brands fa-facebook-f text-white text-lg" aria-hidden="true" />
                </div>
              </a>
            ))}
          </div>

          {/* Right Column: Mobile Device Frame Showcase */}
          <div className="flex justify-center w-full">
            <div className="relative w-[270px] sm:w-[285px] h-[530px] sm:h-[570px] bg-[#141416] rounded-[42px] border-[6px] border-neutral-700 outline outline-1 outline-neutral-800 shadow-2xl overflow-hidden flex flex-col">
              
              {/* Camera Notch Cutout */}
              <div className="w-full h-6 bg-[#f0f2f5] flex justify-center items-start pt-1.5 shrink-0">
                <div className="w-14 h-3 bg-black rounded-full"></div>
              </div>

              {/* Screen Body */}
              <div className="flex-1 bg-[#f0f2f5] overflow-y-auto text-slate-800 text-xs scrollbar-none">
                
                {/* Facebook Cover Header */}
                <div className="bg-[#1877f2] text-white p-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-white p-1 flex items-center justify-center shrink-0 shadow-xs">
                      <img src={COMPANY_INFO.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xs text-white leading-tight">Sakskær Auto</h3>
                      <p className="text-[10px] text-blue-100">Autoværksted · Varde</p>
                    </div>
                  </div>
                </div>

                {/* FB Action bar */}
                <div className="bg-white px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-700">Følg med på Facebook</span>
                  <a
                    href={COMPANY_INFO.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#1877f2] hover:bg-[#166fe5] text-white font-semibold text-[11px] px-3 py-1 rounded-md transition-colors flex items-center gap-1"
                  >
                    <i className="fa-brands fa-facebook-f text-[10px]" aria-hidden="true" />
                    Følg
                  </a>
                </div>

                {/* Facebook Post Feed Preview */}
                <div className="p-2.5 space-y-2.5">
                  {/* Post 1 */}
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-2xs space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-100 p-0.5 shrink-0 border border-slate-200">
                        <img src={COMPANY_INFO.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="font-bold text-[11px] text-slate-900 leading-none">Sakskær Auto</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">Opdateret for nylig · <i className="fa-solid fa-earth-americas" aria-hidden="true" /></p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-700 leading-snug">
                      Husk regelmæssig service på din bil! Vi står klar med professionelt udstyr og god service i Varde. 🔧🚗
                    </p>
                    <div className="rounded overflow-hidden aspect-4/3 bg-slate-100">
                      <img 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/6dac88dc-12c9-4e03-855a-45f8167172f2/523175374-17887908078318194-7009705061051321505-n.jpg" 
                        alt="Værksted opdatering" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                      <span className="flex items-center gap-1"><i className="fa-solid fa-thumbs-up text-blue-500" aria-hidden="true" /> Sakskær Auto</span>
                      <a href={COMPANY_INFO.facebookUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1877f2] hover:underline">Se på Facebook</a>
                    </div>
                  </div>

                  {/* Post 2 */}
                  <div className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-2xs space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-100 p-0.5 shrink-0 border border-slate-200">
                        <img src={COMPANY_INFO.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="font-bold text-[11px] text-slate-900 leading-none">Sakskær Auto</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">Klar til nye dæk? · <i className="fa-solid fa-earth-americas" aria-hidden="true" /></p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-700 leading-snug">
                      Gør bilen klar til sæsonen – få tjekket dæk, bremser og aircondition hos os.
                    </p>
                  </div>
                </div>

              </div>

              {/* Bottom Home Indicator */}
              <div className="w-full h-5 bg-[#f0f2f5] flex justify-center items-center shrink-0">
                <div className="w-24 h-1 bg-neutral-800 rounded-full"></div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* Bottom Contact Section */}
      <BottomContactSection />
    </div>
  )
}
